import React from 'react'
import {getOutValsSigned} from '../api/point'

export const FACT_MARK = 'Максимальные часы фактической пиковой нагрузки'
export const PLAN_MARK = 'Плановые часы пиковой нагрузки'

const MSK_OFFSET_MS = 3 * 60 * 60 * 1000

function pad2(n) {
    return String(n).padStart(2, '0')
}

/** День и час слота: «dd.mm.yyyy hh:mm» или ISO, для меток с UTC — Москва. */
export function slotParts(datOut) {
    const s = String(datOut ?? '').trim()
    const dmy = s.match(/^(\d{2})\.(\d{2})\.(\d{4})(?:[T\s](\d{2}))?/)
    if (dmy) return {dayKey: `${dmy[3]}-${dmy[2]}-${dmy[1]}`, hour: dmy[4] == null ? 0 : Number(dmy[4])}

    const dateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (dateOnly) return {dayKey: s, hour: 0}

    const wall = s.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2})/)
    const hasTz = /\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}(:?\d{2})?)$/i.test(s)
    if (wall && !hasTz) return {dayKey: `${wall[1]}-${wall[2]}-${wall[3]}`, hour: Number(wall[4])}

    const d = new Date(datOut)
    if (Number.isNaN(d.getTime())) {
        if (wall) return {dayKey: `${wall[1]}-${wall[2]}-${wall[3]}`, hour: Number(wall[4])}
        return null
    }
    const msk = new Date(d.getTime() + MSK_OFFSET_MS)
    return {
        dayKey: `${msk.getUTCFullYear()}-${pad2(msk.getUTCMonth() + 1)}-${pad2(msk.getUTCDate())}`,
        hour: msk.getUTCHours(),
    }
}

function holidayDaySet(holidays) {
    return new Set(
        (holidays || [])
            .filter((h) => Number(h.kod_type_day) > 0)
            .map((h) => slotParts(h.d)?.dayKey || String(h.d ?? '').slice(0, 10))
            .filter(Boolean)
    )
}

/** Часы максимума и выходные договора без синхронизации точек. */
export async function loadPeakOverlay(kodDog, period1, period2) {
    if (!kodDog) return {series: [], holidays: []}
    const res = await getOutValsSigned([], period1, period2, true, {flagsOnly: true, kodDog})
    if (!res.isOk) return {series: [], holidays: []}
    return {
        series: res.data?.series || [],
        holidays: res.data?.holidays || [],
    }
}

function isFact(row) {
    return Number(row?.old_power) === 4
}

function isPlan(row) {
    return [3, 4].includes(Number(row?.new_power))
}

/** Поля столбцов часов максимума на строках профиля. Высота столбца — максимум ряда. */
export function withPeakMarks(rows, overlay) {
    const list = rows || []
    const byHour = new Map()
    for (const r of overlay?.series || []) {
        const p = slotParts(r.dat_out)
        if (!p) continue
        byHour.set(`${p.dayKey}|${p.hour}`, r)
    }
    let max = 0
    for (const row of list) {
        const v = Number(row.value ?? row.valueExcell)
        if (Number.isFinite(v) && v > max) max = v
    }
    if (max <= 0 || byHour.size === 0) return list
    return list.map((row) => {
        const p = slotParts(row.dat_local || row.time || '')
        if (!p) return row
        const peak = byHour.get(`${p.dayKey}|${p.hour}`)
        if (!peak) return row
        const next = {...row}
        if (isFact(peak)) next[FACT_MARK] = max
        if (isPlan(peak)) next[PLAN_MARK] = max
        return next
    })
}

/** Полосы выходных по фактическим аргументам оси (дискретные подписи времени). */
export function holidayStrips(rows, holidays, argumentField) {
    const days = holidayDaySet(holidays)
    if (!days.size) return []
    const byDay = new Map()
    for (const row of rows || []) {
        const p = slotParts(row.dat_local || row.time || '')
        if (!p || !days.has(p.dayKey)) continue
        const arg = row[argumentField]
        if (arg == null || arg === '') continue
        const minute = String(row.dat_local || row.time || '').match(/(\d{2}):(\d{2})\s*$/)
        const sort = `${p.dayKey}${pad2(p.hour)}${minute ? minute[2] : '00'}`
        const prev = byDay.get(p.dayKey)
        if (!prev) {
            byDay.set(p.dayKey, {start: arg, end: arg, min: sort, max: sort})
        } else {
            if (sort < prev.min) {
                prev.min = sort
                prev.start = arg
            }
            if (sort > prev.max) {
                prev.max = sort
                prev.end = arg
            }
        }
    }
    return [...byDay.entries()].map(([day, band]) => ({
        day,
        start: band.start,
        end: band.end,
    }))
}

export const HOLIDAY_STRIP_COLOR = 'rgba(255, 192, 203, 0.55)'

/** Полосы выходных для оси datetime: от 00:00 дня до 00:00 следующего (местное время). */
export function holidayDateStrips(holidays) {
    return [...holidayDaySet(holidays)].sort().map((day) => {
        const [y, m, d] = day.split('-').map(Number)
        return {day, start: new Date(y, m - 1, d), end: new Date(y, m - 1, d + 1)}
    })
}

export function hasPeakMark(rows, name) {
    return (rows || []).some((r) => r[name] != null)
}

export function PeakMarksCaption() {
    return (
        <div style={{fontSize: 12, color: '#555', marginTop: 6, lineHeight: 1.6}}>
            <span style={{display: 'inline-block', width: 14, height: 10, background: '#ffc0cb', marginRight: 4, verticalAlign: 'middle'}}/>
            выходные и праздники
            <span style={{display: 'inline-block', width: 14, height: 10, background: '#fbc02d', margin: '0 4px 0 12px', verticalAlign: 'middle'}}/>
            фактические часы максимума
            <span style={{display: 'inline-block', width: 14, height: 10, background: '#66bb6a', margin: '0 4px 0 12px', verticalAlign: 'middle'}}/>
            плановые часы максимума
        </div>
    )
}
